import type { ExplorerFinding } from '../../../../models/explorer/ExplorerFinding';
import type { FindingRule, FindingRuleContext } from '../types';
import { propsOf, strProp } from './_graphHelpers';

/**
 * Tier 1.3 — surface AWS Config rule evaluation results as findings.
 *
 * AWS Config can run hundreds of pre-built and custom rules
 * (CIS / SOC2 / PCI / HIPAA frameworks plus user-defined). The Config
 * parser now hoists `complianceType` + `configRuleList` to first-class
 * `props.complianceType` / `props.configRuleList`. Each
 * NON_COMPLIANT-marked Config rule becomes one finding here, mapped
 * by name → severity. Quiet on resources whose scan didn't include
 * Config — the rule never invents a finding.
 *
 * Severity heuristic:
 *   - Names containing "encrypt", "publicly accessible", "public read",
 *     "iam", "root", "admin"  → critical
 *   - Names containing "logging", "backup", "rotation", "tagging",
 *     "lifecycle"              → warn
 *   - Anything else            → info
 *
 * Rule ids are stable as
 * `aws_config:<configRuleName>:<resource id>` so the recommendation
 * engine groups duplicates across resources cleanly.
 */
export const configRuleComplianceRule: FindingRule = {
  id: 'aws_config_compliance',
  label: 'AWS Config rule violation',
  description: 'A resource is marked NON_COMPLIANT by an AWS Config rule.',

  run(ctx: FindingRuleContext): readonly ExplorerFinding[] {
    const findings: ExplorerFinding[] = [];
    for (const node of ctx.graph.nodes) {
      const p = propsOf(node);
      const overall = strProp(p, ['complianceType']);
      const list = p.configRuleList;
      if (!Array.isArray(list)) continue;

      for (const entry of list) {
        if (!entry || typeof entry !== 'object') continue;
        const e = entry as Record<string, unknown>;
        const compliance =
          (e.ComplianceType as string | undefined) ??
          (e.complianceType as string | undefined) ??
          overall;
        if (!compliance || compliance === 'COMPLIANT' || compliance === 'NOT_APPLICABLE') {
          continue;
        }
        const ruleName =
          (e.ConfigRuleName as string | undefined) ??
          (e.configRuleName as string | undefined) ??
          (e.ConfigRuleArn as string | undefined) ??
          'config-rule';
        const severity = severityFor(ruleName);
        findings.push({
          id: `aws_config_compliance:${ruleName}:${node.id}`,
          ruleId: 'aws_config_compliance',
          severity,
          scope: 'resource',
          subject: { kind: 'resource', id: node.id },
          title: `Config: ${prettyRuleName(ruleName)}`,
          detail: `${String(node.label ?? node.id)} is ${compliance.toLowerCase()} for AWS Config rule \`${ruleName}\`.`,
          attrs: { configRuleName: ruleName, complianceType: compliance },
        });
      }
    }
    return findings;
  },
};

const CRITICAL_HINTS = ['encrypt', 'publicly', 'public-read', 'iam', 'root', 'admin', 'mfa'];
const WARN_HINTS = ['logging', 'backup', 'rotation', 'tagging', 'lifecycle', 'patch'];

function severityFor(ruleName: string): 'critical' | 'warn' | 'info' {
  const n = ruleName.toLowerCase();
  if (CRITICAL_HINTS.some((h) => n.includes(h))) return 'critical';
  if (WARN_HINTS.some((h) => n.includes(h))) return 'warn';
  return 'info';
}

function prettyRuleName(name: string): string {
  return name.replace(/^aws-/, '').replace(/-/g, ' ');
}
